import Dexie from 'dexie';
import { toast } from 'react-toastify';

let db;
let isStorageAvailable = true;
export const LOGGER_DAYS_TO_KEEP = 8;

export const originalConsole = { ...console };

// Overwrite global console methods
["log", "info", "warn", "error"].forEach((level) => {
  console[level] = (...args) => {
    // Still output to the real browser console
    originalConsole[level](...args);
    // Save to IndexedDB
    Logger.saveLog(level, args);
  };
});


// We handle global errors and unhandled promise rejections to ensure that any 
// errors that occur during the service worker registration process are also captured and logged by our Logger. 
window.onerror = (message, source, lineno, colno, error) => {
  console.error(`Error during service worker registration: ${message}`,
    { cause: error });
};

window.onunhandledrejection = (event) => {
  console.error(`Unhandled Promise Rejection: ${event.reason}`);
};

const formatArg = (arg, cache = new Set()) => {
  /*
  This function is responsible for formatting any argument passed to the logger in a way that preserves as much useful information as possible, especially for Error objects. 
  It's designed to be able to handle any type of argument, whether it's a simple string, a complex object, an Error with nested causes, or a list containing any of the previously listed elements.
  It also handles circular references and ensures that we don't run into infinite loops when trying to stringify complex objects. 
  */
  // Handle Null/Undefined/Primitives
  if (arg === null || typeof arg !== 'object') {
    return arg;
  }

  // Handle Circular References
  if (cache.has(arg)) {
    return "[Circular]";
  }
  cache.add(arg);

  // Handle Error Objects (The "Special Sauce")
  if (arg instanceof Error) {
    return {
      name: arg.name,
      message: arg.message,
      stack: arg.stack || arg.prototype?.stack || "No stack trace available",
      cause: arg.cause ? formatArg(arg.cause, cache) : undefined
    };
  }

  // Handle Arrays' items one by one
  if (Array.isArray(arg)) {
    return arg.map(item => formatArg(item, cache));
  }

  // Handle Regular Objects (Recursive)
  const formattedObj = {};
  // We use Object.getOwnPropertyNames to ensure we don't miss anything
  for (const key of Object.getOwnPropertyNames(arg)) {
    formattedObj[key] = formatArg(arg[key], cache);
  }
  return formattedObj;
};

try {
  db = new Dexie('WmeterAppLogsDB');
  db.version(1).stores({
    logs: '++id, timestamp, level'
  });
} catch (e) {
  console.error("IndexedDB initialization failed", e);
  toast.error("A logolási rendszer felállítása nem sikerült, a logok nem lesznek elmentve. Kérjük ellenőrizze a fejlesztői konzolt további részletekért.");
  isStorageAvailable = false;
}


export const Logger = {
  async saveLog(level, args) {
    if (!isStorageAvailable) return;

    try {
      const [firstArg, ...extraArgs] = args;

      const processedMessage = typeof firstArg === 'string'
        ? firstArg
        : JSON.stringify(formatArg(firstArg));

      const processedDetails = extraArgs.map(arg => formatArg(arg));

      await db.logs.add({
        timestamp: new Date().toISOString(),
        level,
        message: processedMessage,
        // We store details as a stringified JSON to preserve the deep structure.
        details: JSON.stringify(processedDetails),
      });
    } catch (err) {
      // We use originalConsole here so we don't trigger an infinite loop.
      originalConsole.error("Logging system failed to save log", err);
    }
  },

  // Delete logs older than 8 days to prevent unlimited growth.
  async cleanOldLogs() {
    if (!isStorageAvailable) return;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - LOGGER_DAYS_TO_KEEP);
    await db.logs.where('timestamp').below(cutoffDate.toISOString()).delete();
    console.info(`Cleaned logs older than ${LOGGER_DAYS_TO_KEEP} days.`);
  },

  async clearLogs() {
    if (!isStorageAvailable) {
      toast.error("A logok törlése nem lehetséges, mert az IndexedDB nem elérhető.");
      return;
    }
    // Calculate size before clearing
    const allLogs = await db.logs.toArray();
    const dataSize = new Blob([JSON.stringify(allLogs)]).size;
    const sizeInKB = (dataSize / 1024).toFixed(2);
    const sizeInMB = (dataSize / (1024 * 1024)).toFixed(2);

    await db.logs.clear();

    const sizeText = dataSize > 1024 * 1024
      ? `${sizeInMB} MB`
      : `${sizeInKB} KB`;

    toast.info(`Az összes elmentett log törölve. Nagyjából ${sizeText} tárhely felszabadítva`);
    console.info(`Internal logs have been cleared manually. Space freed: ${sizeText}`);
  },

  async exportLogs() {
    if (!isStorageAvailable) {
      toast.error("A logok exportálása nem lehetséges, mert az IndexedDB nem elérhető.");
      return;
    }
    const allLogs = await db.logs.toArray();
    const data = JSON.stringify(allLogs, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
